/* ============================================
   GenerarCotizacionModal — Task Selection Wizard
   Selects tasks from a task quotation and generates
   a linked standard quotation.
   ============================================ */
import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSupabase } from '@/hooks/useSupabase'
import { showToast } from '@/components/layout/Toast'
import { formatCurrency, nextCotNumber, r2 } from '@/lib/utils'
import type { Cotizacion, CotizacionItemsEstandar, CotizacionTarea } from '@/lib/types'

interface GenerarCotizacionModalProps {
  isOpen: boolean
  onClose: () => void
  cotOrigen: Cotizacion
  tareas: CotizacionTarea[]
  moneda: string
}

export default function GenerarCotizacionModal({
  isOpen, onClose, cotOrigen, tareas, moneda,
}: GenerarCotizacionModalProps) {
  const db = useSupabase()
  const nav = useNavigate()
  const [selected, setSelected] = useState<boolean[]>(() => tareas.map(() => false))
  const [saving, setSaving] = useState(false)

  // ─── Derived calculations ───
  const selectedCount = selected.filter(Boolean).length
  const allSelected = selectedCount === tareas.length && tareas.length > 0

  const financials = useMemo(() => {
    const selectedTareas = tareas.filter((_, i) => selected[i])
    const subtotal = r2(selectedTareas.reduce((s, t) => s + t.monto, 0))
    const igvPct = cotOrigen.igv_porcentaje
    const descPct = cotOrigen.descuento_porcentaje
    const igvMonto = r2(subtotal * igvPct / 100)
    const descMonto = r2(subtotal * descPct / 100)
    const total = r2(subtotal + igvMonto - descMonto)
    return { subtotal, igvPct, igvMonto, descPct, descMonto, total }
  }, [selected, tareas, cotOrigen])

  // ─── Toggle handlers ───
  const toggleAll = () => {
    const next = !allSelected
    setSelected(tareas.map(() => next))
  }

  const toggleOne = (idx: number) => {
    setSelected(prev => prev.map((v, i) => i === idx ? !v : v))
  }

  // ─── Generate standard quotation ───
  const handleGenerar = async () => {
    if (selectedCount === 0) {
      showToast('Selecciona al menos una tarea', 'error')
      return
    }
    setSaving(true)
    try {
      // Get next number
      const allCot = await db.getAll<Cotizacion>('cotizaciones')
      const numero = nextCotNumber(allCot.map(q => q.numero))

      // Convert selected tasks → standard items
      const selectedIndices = selected.map((v, i) => v ? i : -1).filter(i => i >= 0)
      const selectedTareas = selectedIndices.map(i => tareas[i])

      const items: CotizacionItemsEstandar[] = selectedTareas.map(t => ({
        descripcion: t.titulo,
        precio_unitario: t.monto,
        cantidad: 1,
        total: t.monto,
      }))

      // Build standard quotation data — copy client data from origin
      const data: Record<string, unknown> = {
        numero,
        empresa_id: cotOrigen.empresa_id,
        cliente_id: cotOrigen.cliente_id,
        cliente_nombre: cotOrigen.cliente_nombre,
        cliente_contacto: cotOrigen.cliente_contacto,
        cliente_telefono: cotOrigen.cliente_telefono,
        cliente_ruc: cotOrigen.cliente_ruc,
        cliente_email: cotOrigen.cliente_email,
        fecha_emision: new Date().toISOString().split('T')[0],
        validez_dias: cotOrigen.validez_dias,
        proyecto_nombre: cotOrigen.proyecto_nombre,
        items,
        subtotal: financials.subtotal,
        igv_porcentaje: financials.igvPct,
        igv_monto: financials.igvMonto,
        descuento_porcentaje: financials.descPct,
        descuento_monto: financials.descMonto,
        total: financials.total,
        moneda: cotOrigen.moneda,
        estado: 'borrador',
        metodos_pago: cotOrigen.metodos_pago || ['Transferencia bancaria'],
        secciones: cotOrigen.secciones || [],
        notas: cotOrigen.notas
          ? `${cotOrigen.notas}\n\n[Generada desde Propuesta por Tareas #${cotOrigen.numero}]`
          : `[Generada desde Propuesta por Tareas #${cotOrigen.numero}]`,
        tipo_cotizacion: 'estandar',
        // Origin tracking
        cotizacion_origen_id: cotOrigen.id,
        tareas_seleccionadas: selectedIndices,
      }

      const newId = await db.add('cotizaciones', data)
      showToast(`Cotización estándar #${numero} creada con ${selectedCount} tarea(s)`, 'success')
      onClose()
      nav(`/cotizaciones/${newId}/editar`)
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al generar cotización', 'error')
    } finally {
      setSaving(false)
    }
  }

  if (!isOpen) return null

  return (
    <>
      <div className="modal-overlay" onClick={onClose} />
      <div className="modal" style={{ maxWidth: 720 }}>
        {/* Header */}
        <div className="modal-header">
          <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Generar Cotización Estándar</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Subtitle */}
        <div style={{ padding: '0 24px', marginBottom: 16 }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', margin: 0 }}>
            Selecciona las tareas del catálogo que el cliente quiere. Se generará una cotización estándar vinculada con las tareas seleccionadas.
          </p>
        </div>

        {/* Select all */}
        <div style={{
          padding: '8px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderBottom: '1px solid var(--border)',
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              style={{ width: 16, height: 16, accentColor: 'var(--primary)' }}
            />
            Seleccionar todas ({tareas.length})
          </label>
          <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
            {selectedCount} de {tareas.length} seleccionadas
          </span>
        </div>

        {/* Task list */}
        <div style={{ padding: '0 24px', maxHeight: 380, overflowY: 'auto' }}>
          {tareas.map((t, idx) => (
            <div
              key={idx}
              onClick={() => toggleOne(idx)}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '12px 0',
                borderBottom: '1px solid var(--border)',
                cursor: 'pointer',
                opacity: selected[idx] ? 1 : 0.6,
                transition: 'opacity 0.15s',
              }}
            >
              <input
                type="checkbox"
                checked={selected[idx]}
                onChange={() => toggleOne(idx)}
                onClick={e => e.stopPropagation()}
                style={{ width: 16, height: 16, flexShrink: 0, accentColor: 'var(--primary)' }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>
                  <span style={{ color: 'var(--text-secondary)', fontWeight: 400, marginRight: 6 }}>#{idx + 1}</span>
                  {t.titulo}
                </div>
                {t.descripcion_corta && (
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {t.descripcion_corta}
                  </div>
                )}
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                  {formatCurrency(t.monto, moneda)}
                </div>
                {t.horas_estimadas != null && t.horas_estimadas > 0 && (
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                    {t.horas_estimadas}h est.
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Financial preview */}
        <div style={{
          padding: '16px 24px', background: 'var(--bg-secondary)',
          borderTop: '1px solid var(--border)', marginTop: 8,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '0.85rem' }}>
            <span>Subtotal ({selectedCount} tarea{selectedCount !== 1 ? 's' : ''})</span>
            <strong>{formatCurrency(financials.subtotal, moneda)}</strong>
          </div>
          {financials.descPct > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '0.85rem' }}>
              <span>Descuento ({financials.descPct}%)</span>
              <strong style={{ color: 'var(--success)' }}>−{formatCurrency(financials.descMonto, moneda)}</strong>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '0.85rem' }}>
            <span>IGV ({financials.igvPct}%)</span>
            <strong>{formatCurrency(financials.igvMonto, moneda)}</strong>
          </div>
          <div style={{
            display: 'flex', justifyContent: 'space-between', padding: '8px 0', marginTop: 4,
            borderTop: '1px solid var(--border)', fontSize: '1.05rem', color: 'var(--success)',
          }}>
            <span style={{ fontWeight: 700 }}>TOTAL</span>
            <strong>{formatCurrency(financials.total, moneda)}</strong>
          </div>
        </div>

        {/* Actions */}
        <div style={{ padding: '16px 24px', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancelar</button>
          <button
            className="btn btn-primary"
            onClick={handleGenerar}
            disabled={saving || selectedCount === 0}
          >
            {saving
              ? <><div className="auth-spinner" style={{ width: 14, height: 14 }} /> Generando...</>
              : <>Generar Cotización ({selectedCount})</>
            }
          </button>
        </div>
      </div>
    </>
  )
}
